import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import {
  CurrencyDollarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  BanknotesIcon,
  PlusIcon,
  ArrowPathIcon,
  TrashIcon,
  ChartBarIcon,
  DocumentChartBarIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { journalEntriesService } from '../../../services/accounting/journal-entries';
import toast from 'react-hot-toast';
import CreateJournalEntryModal from '../CreateJournalEntryModal';
import ViewJournalEntryModal from '../ViewJournalEntryModal';
import TrialBalanceModal from '../TrialBalanceModal';
import GeneralLedgerModal from '../GeneralLedgerModal';

/**
 * JournalEntriesTab Component
 * 
 * Comprehensive journal entries management interface that handles:
 * - Journal entry listing with search and filtering
 * - Entry creation, editing, posting, and reversal
 * - Missing entries detection and creation
 * - Duplicate cleanup functionality
 * - Trial balance and general ledger access
 * - Quick entry templates
 * 
 * Features:
 * - Real-time journal entries data loading
 * - Status-based filtering and actions
 * - Entry type categorization with visual badges
 * - Bulk operations for missing entries and duplicates
 * - Financial reporting integration
 * 
 * Translation Support:
 * - All user-facing text uses i18n translation keys
 * - Supports both Arabic and English interfaces
 * - Maintains RTL compatibility
 */
const JournalEntriesTab = () => {
  const { t } = useTranslation();
  const { currentCompany, currentUser } = useAuth();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [processingMissing, setProcessingMissing] = useState(false);
  const [journalEntries, setJournalEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter] = useState('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showTrialBalanceModal, setShowTrialBalanceModal] = useState(false);
  const [showGeneralLedgerModal, setShowGeneralLedgerModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  /**
   * Load journal entries data
   * Handles error cases and provides user feedback
   */
  const loadJournalEntries = useCallback(async () => {
    if (!currentCompany?.id) return;
    setLoading(true);
    try {
      const entriesData = await journalEntriesService.getJournalEntries(currentCompany.id);
      setJournalEntries(entriesData);
    } catch (error) {
      console.error('Error loading journal entries:', error);
      toast.error(t('common.error_loading_data', 'Failed to load journal entries'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, t]);

  /**
   * Create missing journal entries for existing invoices and payments
   * Provides detailed feedback on the operation results
   */
  const handleCreateMissingJournalEntries = async () => {
    if (!currentCompany?.id || !currentUser?.uid) return;
    
    setProcessingMissing(true);
    try {
      console.log('🔄 Starting to create missing journal entries...');
      
      const result = await journalEntriesService.createMissingJournalEntries(
        currentCompany.id, 
        currentUser.uid
      );
      
      console.log('✅ Missing journal entries result:', result);
      
      toast.success(
        t('journal.missing_entries_created', 
          `Created ${result.invoicesProcessed} invoice entries and ${result.paymentsProcessed} payment entries`
        )
      );
      
      // Reload the journal entries to show the new ones
      loadJournalEntries();
      
    } catch (error) {
      console.error('❌ Error creating missing journal entries:', error);
      toast.error(t('journal.error_creating_missing', 'Failed to create missing journal entries: ' + error.message));
    } finally {
      setProcessingMissing(false);
    }
  };

  /**
   * Clean up duplicate journal entries
   * Removes duplicate entries while preserving data integrity
   */
  const handleCleanupDuplicates = async () => {
    if (!currentCompany?.id || !currentUser?.uid) return;
    
    setProcessingMissing(true);
    try {
      console.log('🔄 Starting to clean up duplicate journal entries...');
      
      const result = await journalEntriesService.cleanupDuplicateJournalEntries(
        currentCompany.id, 
        currentUser.uid
      );
      
      console.log('✅ Cleanup result:', result);
      
      if (result.totalDeleted > 0) {
        toast.success(
          t('journal.duplicates_cleaned', 
            `Removed ${result.totalDeleted} duplicate journal entries`
          )
        );
      } else {
        toast(
          t('journal.no_duplicates_found', 
            'No duplicate journal entries found'
          )
        );
      }
      
      // Reload the journal entries to show the cleaned up list
      loadJournalEntries();
      
    } catch (error) {
      console.error('❌ Error cleaning up duplicate journal entries:', error);
      toast.error(
        t('journal.error_cleaning_duplicates', 
          'Failed to remove duplicate journal entries: ' + error.message
        )
      );
    } finally {
      setProcessingMissing(false);
    }
  };

  // Load journal entries on component mount and company change
  useEffect(() => {
    loadJournalEntries();
  }, [loadJournalEntries]);

  /**
   * Entry types configuration with translations and colors
   * Supports comprehensive categorization of journal entries
   */
  const entryTypes = [
    { id: 'manual', name: t('accounting.manual', 'Manual'), color: 'blue' },
    { id: 'automatic', name: t('accounting.automatic', 'Automatic'), color: 'green' },
    { id: 'adjustment', name: t('accounting.adjustment', 'Adjustment'), color: 'yellow' },
    { id: 'accrual', name: t('accounting.accrual', 'Accrual'), color: 'purple' },
    { id: 'correction', name: t('accounting.correction', 'Correction'), color: 'orange' },
    { id: 'closing', name: t('accounting.closing', 'Closing'), color: 'red' },
    { id: 'reversal', name: t('accounting.reversal', 'Reversal'), color: 'gray' },
    { id: 'reclassification', name: t('accounting.reclassification', 'Reclassification'), color: 'indigo' }
  ];

  /**
   * Get type badge component with appropriate styling
   */
  const getTypeBadge = (type) => {
    const typeConfig = entryTypes.find(t => t.id === type) || entryTypes[0];
    return <span className={`badge badge-${typeConfig.color} badge-sm`}>{typeConfig.name}</span>;
  };

  /**
   * Get status badge component with appropriate styling
   */
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { class: 'badge badge-warning badge-sm', text: t('quotations.draft') },
      posted: { class: 'badge badge-success badge-sm', text: t('accounting.posted') },
      reversed: { class: 'badge badge-error badge-sm', text: t('accounting.reversed', 'Reversed') }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    return <span className={config.class}>{config.text}</span>;
  };

  /**
   * Action handlers for journal entry operations
   */
  
  const handleViewEntry = (entry) => {
    setSelectedEntry(entry);
    setShowViewModal(true);
  };

  const handleEditEntry = (entry) => {
    if (entry.status === 'posted') {
      toast.error(t('journal.cannot_edit_posted', 'Cannot edit posted entries. Please reverse first.'));
      return;
    }
    setSelectedEntry(entry);
    setShowEditModal(true);
  };

  const handlePostEntry = async (entryId) => {
    if (!window.confirm(t('journal.confirm_post', 'Are you sure you want to post this entry? This action cannot be undone.'))) {
      return;
    }

    try {
      await journalEntriesService.postJournalEntry(entryId, currentUser.uid);
      toast.success(t('journal.posted_successfully', 'Journal entry posted successfully'));
      loadJournalEntries();
    } catch (error) {
      console.error('Error posting journal entry:', error);
      toast.error(error.message || t('journal.post_failed', 'Failed to post journal entry'));
    }
  };

  const handleReverseEntry = async (entry) => {
    const reason = window.prompt(t('journal.reverse_reason', 'Please enter a reason for reversing this entry:'));
    if (!reason) return;

    try {
      await journalEntriesService.reverseJournalEntry(entry.id, currentUser.uid, reason);
      toast.success(t('journal.reversed_successfully', 'Journal entry reversed successfully'));
      loadJournalEntries();
    } catch (error) {
      console.error('Error reversing journal entry:', error);
      toast.error(error.message || t('journal.reverse_failed', 'Failed to reverse journal entry'));
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm(t('clients.confirm_delete'))) {
      return;
    }

    try {
      await journalEntriesService.deleteJournalEntry(entryId);
      toast.success(t('clients.deleted_successfully'));
      loadJournalEntries();
    } catch (error) {
      console.error('Error deleting journal entry:', error);
      toast.error(error.message || t('clients.delete_failed'));
    }
  };

  /**
   * Filter and sort journal entries based on search criteria
   * Ensures newest entries appear first
   */
  const filteredEntries = journalEntries.filter(entry => {
    const matchesSearch = 
      entry.entryNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === 'all' || entry.entryType === typeFilter;
    const matchesStatus = statusFilter === 'all' || entry.status === statusFilter;
    
    return matchesSearch && matchesType && matchesStatus;
  }).sort((a, b) => {
    // Ensure newest entries show on top by sorting by creation time first, then by date
    const createdA = a.createdAt?.toDate?.() || new Date(0);
    const createdB = b.createdAt?.toDate?.() || new Date(0);
    
    // If created at same time, sort by date (newest first)
    if (createdA.getTime() === createdB.getTime()) {
      const dateA = new Date(a.date || 0);
      const dateB = new Date(b.date || 0);
      return dateB - dateA;
    }
    
    return createdB - createdA;
  });

  // Calculate summary statistics for dashboard cards
  const totalEntries = journalEntries.length;
  const draftEntriesCount = journalEntries.filter(entry => entry.status === 'draft').length;
  const postedEntriesCount = journalEntries.filter(entry => entry.status === 'posted').length;
  const totalDebitAmount = journalEntries.reduce((sum, entry) => sum + (entry.totalDebit || 0), 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards - Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body text-center">
            <CurrencyDollarIcon className="w-8 h-8 mx-auto text-blue-600 mb-2" />
            <div className="text-2xl font-bold text-gray-900">{totalEntries}</div>
            <div className="text-sm text-gray-500">{t('journal.total_entries', 'Total Entries')}</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <DocumentTextIcon className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
            <div className="text-2xl font-bold text-yellow-600">{draftEntriesCount}</div>
            <div className="text-sm text-gray-500">{t('journal.draft_entries', 'Draft Entries')}</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <CheckCircleIcon className="w-8 h-8 mx-auto text-green-600 mb-2" />
            <div className="text-2xl font-bold text-green-600">{postedEntriesCount}</div>
            <div className="text-sm text-gray-500">{t('journal.posted_entries', 'Posted Entries')}</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <BanknotesIcon className="w-8 h-8 mx-auto text-purple-600 mb-2" />
            <div className="text-2xl font-bold text-purple-600">SAR {totalDebitAmount.toLocaleString()}</div>
            <div className="text-sm text-gray-500">{t('journal.total_value', 'Total Value')}</div>
          </div>
        </div>
      </div>

      {/* Header with Actions */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('journal.title', 'Journal Entries')}</h2>
          <p className="text-gray-600">{t('journal.subtitle', 'Manage your journal entries and financial transactions')}</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleCreateMissingJournalEntries}
            disabled={processingMissing}
            className="btn btn-outline btn-sm"
          >
            {processingMissing ? (
              <>
                <LoadingSpinner size="sm" />
                {t('journal.creating_missing', 'Creating Missing Entries...')}
              </>
            ) : (
              <>
                <ArrowPathIcon className="w-4 h-4" />
                {t('journal.create_missing', 'Create Missing Entries')}
              </>
            )}
          </button>
          
          <button
            onClick={handleCleanupDuplicates}
            disabled={processingMissing}
            className="btn btn-warning btn-sm"
          >
            {processingMissing ? (
              <>
                <LoadingSpinner size="sm" />
                {t('journal.cleaning_duplicates', 'Cleaning Duplicates...')}
              </>
            ) : (
              <>
                <TrashIcon className="w-4 h-4" />
                {t('journal.cleanup_duplicates', 'Remove Duplicates')}
              </>
            )}
          </button>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary btn-sm"
          >
            <PlusIcon className="w-4 h-4" />
            {t('journal.create_entry', 'Create Entry')}
          </button>
          
          <button
            onClick={() => setShowTrialBalanceModal(true)}
            className="btn btn-secondary btn-sm"
          >
            <ChartBarIcon className="w-4 h-4" />
            {t('journal.trial_balance', 'Trial Balance')}
          </button>
          
          <button
            onClick={() => setShowGeneralLedgerModal(true)}
            className="btn btn-secondary btn-sm"
          >
            <DocumentChartBarIcon className="w-4 h-4" />
            {t('journal.general_ledger', 'General Ledger')}
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  🔍
                </div>
                <input
                  type="text"
                  placeholder={t('accounting.search_placeholder')}
                  className="input input-bordered w-full pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex space-x-4">
              <select
                className="select select-bordered"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="all">{t('journal.filter.all_types', 'All Types')}</option>
                <option value="manual">✏️ {t('accounting.manual')}</option>
                <option value="automatic">🤖 {t('accounting.automatic')}</option>
                <option value="adjustment">⚖️ {t('accounting.adjustment')}</option>
                <option value="accrual">📅 {t('accounting.accrual')}</option>
                <option value="correction">🔧 {t('accounting.correction')}</option>
                <option value="closing">🔒 {t('accounting.closing')}</option>
                <option value="reversal">↩️ {t('accounting.reversal')}</option>
                <option value="reclassification">🔄 {t('accounting.reclassification')}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Journal Entries Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>{t('journal.table.date', t('common.date'))}</th>
                    <th>{t('journal.table.entry_id', 'Entry ID')}</th>
                    <th>{t('journal.table.type', 'Type')}</th>
                    <th>{t('journal.table.description', t('common.description'))}</th>
                    <th>{t('journal.table.debit', 'Debit')}</th>
                    <th>{t('journal.table.credit', 'Credit')}</th>
                    <th>{t('journal.table.status', t('common.status'))}</th>
                    <th>{t('journal.table.actions', t('common.actions'))}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-8">
                        <div className="text-gray-500">
                          {searchTerm || typeFilter !== 'all' ? 
                            t('journal.no_entries_found', 'No journal entries found matching your filters') :
                            t('journal.no_entries', 'No journal entries yet')
                          }
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredEntries.map((entry) => (
                      <tr key={entry.id}>
                        <td>
                          <div className="font-medium">{new Date(entry.date).toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500">
                            {entry.createdAt ? new Date(entry.createdAt.toDate()).toLocaleTimeString() : 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center space-x-2">
                            <div className="font-mono text-sm">{entry.entryNumber}</div>
                            {entry.entryNumber && entry.entryNumber.startsWith('J') && entry.entryNumber.length <= 10 && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                NEW
                              </span>
                            )}
                          </div>
                          {entry.reference && (
                            <div className="text-xs text-gray-500">Ref: {entry.reference}</div>
                          )}
                        </td>
                        <td>{getTypeBadge(entry.entryType)}</td>
                        <td>
                          <div className="max-w-xs truncate" title={entry.description}>
                            {entry.description}
                          </div>
                        </td>
                        <td>
                          <span className="font-semibold text-green-600">
                            SAR {(entry.totalDebit || 0).toFixed(2)}
                          </span>
                        </td>
                        <td>
                          <span className="font-semibold text-red-600">
                            SAR {(entry.totalCredit || 0).toFixed(2)}
                          </span>
                        </td>
                        <td>{getStatusBadge(entry.status)}</td>
                        <td>
                          <div className="flex space-x-1">
                            <button
                              className="btn btn-xs btn-ghost"
                              title={t('journal.view', t('common.view'))}
                              onClick={() => handleViewEntry(entry)}
                            >
                              <EyeIcon className="w-3 h-3" />
                            </button>
                            
                            {entry.status === 'draft' && (
                              <>
                                <button
                                  className="btn btn-xs btn-ghost"
                                  title={t('journal.edit', t('common.edit'))}
                                  onClick={() => handleEditEntry(entry)}
                                >
                                  ✏️
                                </button>
                                <button
                                  className="btn btn-xs btn-success"
                                  title={t('journal.post', 'Post')}
                                  onClick={() => handlePostEntry(entry.id)}
                                >
                                  ✅
                                </button>
                                <button
                                  className="btn btn-xs btn-error"
                                  title={t('journal.delete', t('common.delete'))}
                                  onClick={() => handleDeleteEntry(entry.id)}
                                >
                                  <TrashIcon className="w-3 h-3" />
                                </button>
                              </>
                            )}
                            
                            {entry.status === 'posted' && (
                              <button
                                className="btn btn-xs btn-warning"
                                title={t('journal.reverse', 'Reverse')}
                                onClick={() => handleReverseEntry(entry)}
                              >
                                ↩️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Quick Entry Templates Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">{t('accounting.quick_templates')}</h3>
          <p className="text-sm text-gray-500">{t('journal.templates_description', 'Common journal entry templates for quick access')}</p>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="p-4 border border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left">
              <div className="text-lg mb-2">📉</div>
              <div className="font-medium">{t('accounting.depreciation')}</div>
              <div className="text-xs text-gray-500">{t('journal.templates.depreciation_desc', 'Monthly asset depreciation')}</div>
            </button>
            
            <button className="p-4 border border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left">
              <div className="text-lg mb-2">📅</div>
              <div className="font-medium">{t('accounting.accrual')}</div>
              <div className="text-xs text-gray-500">{t('journal.templates.accrual_desc', 'Record accrued expenses')}</div>
            </button>
            
            <button className="p-4 border border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left">
              <div className="text-lg mb-2">🔧</div>
              <div className="font-medium">{t('accounting.correction')}</div>
              <div className="text-xs text-gray-500">{t('journal.templates.correction_desc', 'Correct misclassified entries')}</div>
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      
      {/* Create Journal Entry Modal */}
      {showCreateModal && (
        <CreateJournalEntryModal 
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadJournalEntries();
          }}
        />
      )}

      {/* Edit Journal Entry Modal */}
      {showEditModal && selectedEntry && (
        <CreateJournalEntryModal 
          entry={selectedEntry}
          onClose={() => {
            setShowEditModal(false);
            setSelectedEntry(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedEntry(null);
            loadJournalEntries();
          }}
        />
      )}

      {/* View Journal Entry Modal */}
      {showViewModal && selectedEntry && (
        <ViewJournalEntryModal 
          entry={selectedEntry}
          onClose={() => {
            setShowViewModal(false);
            setSelectedEntry(null);
          }}
          onEdit={() => {
            setShowViewModal(false);
            setShowEditModal(true);
          }}
        />
      )}

      {/* Trial Balance Modal */}
      {showTrialBalanceModal && (
        <TrialBalanceModal 
          onClose={() => setShowTrialBalanceModal(false)}
        />
      )}

      {/* General Ledger Modal */}
      {showGeneralLedgerModal && (
        <GeneralLedgerModal 
          onClose={() => setShowGeneralLedgerModal(false)}
        />
      )}
    </div>
  );
};

export default JournalEntriesTab;